import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UnifiedRevisionView,
  getUnifiedRevisionHistory,
} from '../../../api/General/businessRoles';
import { Spinner } from '../../../components/ui/Spinner';
import { humanizeFieldLabel } from '../../../utils/humanizeFieldLabel';

interface RevisionHistoryPanelProps {
  roleId: number;
}

export default function RevisionHistoryPanel({ roleId }: RevisionHistoryPanelProps) {
  const { t } = useTranslation();
  const [revisions, setRevisions] = useState<UnifiedRevisionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getUnifiedRevisionHistory(roleId);
      if (result.errors.length === 0) {
        setRevisions(result.payload);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    if (expanded) {
      loadHistory();
    }
  }, [expanded, loadHistory]);

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'assignment':
        return (
          <div className="w-7 h-7 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
        );
      case 'revocation':
        return (
          <div className="w-7 h-7 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
            </svg>
          </div>
        );
      default: // definition_change
        return (
          <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
        );
    }
  };

  const getEventLabel = (rev: UnifiedRevisionView) => {
    switch (rev.eventType) {
      case 'assignment':
        return t('businessRoles.revisionAssigned', { employee: rev.employeeName || rev.employeeId });
      case 'revocation':
        return t('businessRoles.revisionRevoked', { employee: rev.employeeName || rev.employeeId });
      default: {
        // Map the raw C# field name (e.g. "RequiredSkillItems") to a friendly
        // label; fall back to a humanized version so no identifier leaks through.
        const fieldLabel = rev.fieldName
          ? t(`businessRoles.revisionField.${rev.fieldName}`, { defaultValue: humanizeFieldLabel(rev.fieldName) })
          : '';
        return t('businessRoles.revisionChanged', { field: fieldLabel });
      }
    }
  };

  const formatFieldValue = (value: string | undefined) => {
    if (!value) return '-';
    // Try to parse JSON arrays for display
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
    } catch { /* not JSON */ }
    // Truncate long values
    return value.length > 80 ? value.substring(0, 80) + '...' : value;
  };

  // Group revisions by date
  const groupedRevisions: Record<string, UnifiedRevisionView[]> = {};
  revisions.forEach(rev => {
    const dateKey = new Date(rev.changedAt).toLocaleDateString();
    if (!groupedRevisions[dateKey]) groupedRevisions[dateKey] = [];
    groupedRevisions[dateKey].push(rev);
  });

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left min-h-[44px]"
      >
        <svg
          className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <h3 className="text-sm font-semibold text-foreground">{t('businessRoles.revisionHistory')}</h3>
        {revisions.length > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs bg-muted text-muted-foreground">
            {revisions.length}
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-3">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="sm" />
            </div>
          ) : revisions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('businessRoles.noRevisions')}</p>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedRevisions).map(([date, items]) => (
                <div key={date}>
                  <p className="text-xs font-medium text-muted-foreground uppercase mb-2">{date}</p>
                  <div className="space-y-2">
                    {items.map((rev) => (
                      <div key={rev.id} className="flex gap-3 p-3 rounded-lg border border-border bg-muted hover:bg-accent transition-colors">
                        {getEventIcon(rev.eventType)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground">{getEventLabel(rev)}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span>{rev.changedByName || rev.changedById}</span>
                            <span>{'\u2022'}</span>
                            <span>{new Date(rev.changedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>

                          {/* Show field changes for definition changes */}
                          {rev.eventType === 'definition_change' && (rev.oldValue || rev.newValue) && (
                            <div className="mt-2 text-xs space-y-0.5">
                              {rev.oldValue && (
                                <p className="text-destructive">
                                  <span className="font-medium">-</span> {formatFieldValue(rev.oldValue)}
                                </p>
                              )}
                              {rev.newValue && (
                                <p className="text-success">
                                  <span className="font-medium">+</span> {formatFieldValue(rev.newValue)}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Comment */}
                          {rev.comment && (
                            <p className="mt-1 text-xs text-muted-foreground italic">{rev.comment}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
